<div class="flex flex-col gap-3" data-cy="medical-record-list">
  <div class="flex flex-wrap items-start justify-between gap-3">
    <div class="flex flex-col">
      <h3 class="medical-section-title text-lg font-semibold m-0">{{ 'animalMedical.records.headline' | transloco }}</h3>
      <span class="text-sm text-gray-500">{{ 'animalMedical.records.immutableHint' | transloco }}</span>
    </div>
    <p-button
      icon="pi pi-plus"
      [label]="'animalMedical.records.create' | transloco"
      data-cy="medical-record-create"
      (onClick)="create.emit()"
    />
  </div>

  @if (records.length) {
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex flex-wrap gap-2">
        <p-button
          size="small"
          [rounded]="true"
          [outlined]="typeFilter !== 'all'"
          [label]="'animalMedical.records.allTypes' | transloco"
          (onClick)="setTypeFilter('all')"
        />
        @for (type of availableTypes; track type) {
          <p-button
            size="small"
            [rounded]="true"
            [outlined]="typeFilter !== type"
            [label]="recordTypeLabelKey(type) | transloco"
            (onClick)="setTypeFilter(type)"
          />
        }
      </div>
      <span class="text-sm text-gray-500">
        {{ 'animalMedical.records.countOf' | transloco: { count: filteredRecords.length, total: currentRecords.length } }}
      </span>
    </div>
  }

  @if (loaded && !records.length) {
    <p-message severity="info">{{ 'animalMedical.records.empty' | transloco }}</p-message>
  }

  @for (record of pagedRecords; track record.id) {
    <div class="flex flex-col gap-2">
      <ng-container *ngTemplateOutlet="recordCard; context: { $implicit: record }" />
      @if (history[record.id]?.length) {
        <div class="flex flex-col gap-2 ml-4">
          <p-button
            size="small"
            [text]="true"
            severity="secondary"
            class="self-start"
            [icon]="expandedHistory.has(record.id) ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"
            [label]="'animalMedical.records.history' | transloco: { count: history[record.id].length }"
            [attr.data-cy]="'medical-record-history-toggle-' + record.id"
            (onClick)="toggleHistory(record.id)"
          />
          @if (expandedHistory.has(record.id)) {
            <div class="flex flex-col gap-2 pl-3 border-l-2 border-gray-200">
              @for (previous of history[record.id]; track previous.id) {
                <ng-container *ngTemplateOutlet="recordCard; context: { $implicit: previous }" />
              }
            </div>
          }
        </div>
      }
    </div>
  }

  @if (filteredRecords.length > pageSize) {
    <p-paginator
      [first]="first"
      [rows]="pageSize"
      [totalRecords]="filteredRecords.length"
      (onPageChange)="onPageChange($event)"
    />
  }
</div>

<ng-template #recordCard let-record>
  <article
    [id]="'medical-record-' + record.id"
    class="border rounded-lg p-3 flex flex-col gap-2 transition-colors"
    [ngClass]="{
      'opacity-60 bg-gray-50 border-gray-200': isCorrected(record),
      'border-gray-200': !isCorrected(record) && flashRecordId !== record.id,
      'border-[var(--p-primary-400)] bg-[var(--p-primary-50)]': flashRecordId === record.id
    }"
    [attr.data-cy]="'medical-record-' + record.id"
  >
    <header class="flex flex-wrap items-center justify-between gap-2">
      <div class="flex flex-wrap items-center gap-2 text-sm">
        <span class="font-semibold">
          {{ record.date | date:'dd.MM.yyyy HH:mm' }}
        </span>
        @if (record.type) {
          <p-tag [severity]="typeSeverity(record.type)" [value]="recordTypeLabelKey(record.type) | transloco" />
        }
        @if (veterinarianName(record); as vet) {
          <span class="flex items-center gap-1 text-gray-600"><i class="pi pi-user text-xs"></i>{{ vet }}</span>
        }
        @if (record.weight !== null) {
          <span class="flex items-center gap-1 text-gray-600">
            <i class="pi pi-gauge text-xs"></i>{{ gramsToKg(record.weight) | number:'1.0-3' }} kg
          </span>
        }
        @if (isCorrected(record)) {
          <p-tag severity="secondary" icon="pi pi-history"
                 [value]="correctedBy[record.id]
                   ? ('animalMedical.records.correctedBy' | transloco: { id: correctedBy[record.id] })
                   : ('animalMedical.records.corrected' | transloco)" />
        }
        @if (correctsId(record); as correctsId) {
          <p-tag severity="info" icon="pi pi-replay"
                 [value]="'animalMedical.records.corrects' | transloco: { id: correctsId }" />
        }
      </div>
      <div class="flex items-center gap-2">
        <span class="text-xs text-gray-500">#{{ record.id }} · {{ creatorName(record) }}</span>
        @if (!isCorrected(record)) {
          <p-button
            size="small"
            [text]="true"
            icon="pi pi-pencil"
            [label]="'animalMedical.records.correct' | transloco"
            [attr.data-cy]="'medical-record-correct-' + record.id"
            (onClick)="correct.emit(record)"
          />
        }
      </div>
    </header>

    @if (record.finding || record.treatment) {
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        @if (record.finding) {
          <div>
            <div class="text-xs text-gray-500">{{ 'animalMedical.fields.finding' | transloco }}</div>
            <div class="whitespace-pre-line">{{ record.finding }}</div>
          </div>
        }
        @if (record.treatment) {
          <div>
            <div class="text-xs text-gray-500">{{ 'animalMedical.fields.treatment' | transloco }}</div>
            <div class="whitespace-pre-line">{{ record.treatment }}</div>
          </div>
        }
      </div>
    }

    @if (record.bcsNutrition || record.careCondition?.length) {
      <div class="flex flex-wrap gap-2">
        @if (nutritionLabel(record.bcsNutrition); as nutrition) {
          <p-chip styleClass="text-xs" [label]="('bcs.nutrition.title' | transloco) + ': ' + (nutrition | transloco)" />
        }
        @for (care of record.careCondition ?? []; track care) {
          <p-chip styleClass="text-xs" [label]="('bcs.care.title' | transloco) + ': ' + (careLabel(care) | transloco)" />
        }
      </div>
    }

    @if (positions[record.id]?.treatments?.length || positions[record.id]?.conditions?.length) {
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-xs text-gray-500">{{ 'animalMedical.records.createdWith' | transloco }}</span>
        @for (treatment of positions[record.id].treatments; track treatment.id) {
          <p-chip styleClass="text-xs" icon="pi pi-shield">
            <span class="text-xs">
              {{ categoryLabelKey(treatment.category) | transloco }}@if (treatmentTypes(treatment).length) { ·
                @for (type of treatmentTypes(treatment); track type; let last = $last) {
                  {{ typeLabelKey(type) | transloco }}@if (!last) {, }
                }
              }
              @if (treatment.preparation) { ({{ treatment.preparation }}) }
            </span>
          </p-chip>
        }
        @for (condition of positions[record.id].conditions; track condition.id) {
          <p-chip styleClass="text-xs" icon="pi pi-heart">
            <span class="text-xs">{{ conditionKindLabelKey(condition.kind) | transloco }} · {{ condition.label }}</span>
          </p-chip>
        }
      </div>
    }

    @if (changeChips[record.id]?.length) {
      <div class="flex flex-wrap items-center gap-2" [attr.data-cy]="'medical-record-changes-' + record.id">
        <span class="text-xs text-gray-500">{{ 'animalMedical.records.continued' | transloco }}</span>
        @for (chip of changeChips[record.id]; track $index) {
          <p-chip styleClass="text-xs" [icon]="chip.icon">
            <span class="text-xs">
              @for (part of chip.target; track $index; let last = $last) {
                <ng-container *ngTemplateOutlet="label; context: { $implicit: part }" />@if (!last) {, }
              }
              · <ng-container *ngTemplateOutlet="label; context: { $implicit: chip.field }" />:
              <ng-container *ngTemplateOutlet="label; context: { $implicit: chip.from }" />
              →
              <span class="font-semibold"><ng-container *ngTemplateOutlet="label; context: { $implicit: chip.to }" /></span>
            </span>
          </p-chip>
        }
      </div>
    }

    @if (files[record.id]?.length) {
      <div class="flex flex-wrap items-center gap-2">
        <span class="text-xs text-gray-500">{{ 'animalMedical.records.files' | transloco }}</span>
        @for (file of files[record.id]; track file.key) {
          <button
            type="button"
            class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-gray-300 text-xs hover:bg-gray-100 cursor-pointer max-w-64"
            (click)="openFile.emit(file)"
          >
            <i [class]="fileIcon(file)"></i>
            <span class="truncate">{{ file.name }}</span>
          </button>
        }
      </div>
    }
  </article>
</ng-template>

<ng-template #label let-label>{{ label.key ? (label.key | transloco) : label.text }}</ng-template>
